import React, { useState, useEffect, useRef } from "react";
import {
	utcToLocalDateTime,
	utcToDate,
	BLOCK_REASON_REQUIRED_TEXT,
	isBlockReasonValid,
	normalizeBlockReason,
} from "../../components/helper";
import { useTranslation } from "../../contexts/TranslationContext";
import "./AllProfiles.scss";
import {
	endOrders,
	getPhotos,
	deletePhoto,
	deleteSelfie,
	sendToTelegram,
	blockUser,
	getVideo,
	setPhoto,
	getSelfie,
	deleteVideo,
	deleteUser,
	getUsersMod,
	getUsersModDetails,
	recomputeFaceMatch,
} from "../../api/apiServiceFetch";
import ConfirmPopup from "../../components/ConfirmPopup";
import Popup from "../../components/Popup";
import OrderPopup from "../../components/OrderPopup";
import UserFirstUsePopup from "../../components/UserFirstUsePopup";
import PhotoModal from "../../components/PhotoModal";
import PopupMessage from "../../components/PopupMessage";
import PopupGift from "../../components/PopupGift";
import { useAuth } from "../../contexts/auth";
import { getFileBaseUrl } from "../../config/environment";

const ITEMS_PER_PAGE = 30;
const AllProfiles = () => {
	const { userBase } = useAuth();
	const { t } = useTranslation();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [isUserFirstUsePopupOpen, setUserFirstUsePopupOpen] = useState(false);
	const [isOrderPopupOpen, setIsOrderPopupOpen] = useState(false);
	const textareaRef = useRef(null);
	const [userButtonData, setUserButtonData] = useState(null);
	const [editUser, setEditUser] = useState();
	const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
	const [bigger, setBigger] = useState(false);
	const [users, setUsers] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [expandedUserId, setExpandedUserId] = useState(null);
	const [expandedUserLang, setExpandedUserLang] = useState("ru");
	const [videoUrl, setVideoUrl] = useState("");
	const [videoId, setVideoId] = useState();
	const [videoIsModerated, setVideoIsModerated] = useState(false);
	const videoRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isMuted, setIsMuted] = useState(true);

	const [filterTerm, setFilterTerm] = useState("");
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [filterCountry, setFilterCountry] = useState("");
	const [filterCity, setFilterCity] = useState("");
	const [filterSex, setFilterSex] = useState(0);
	const [userPhotos, setUserPhotos] = useState([]);
	const [userSelfie, setUserSelfie] = useState();

	const [message, setMessage] = useState("");
	const [reason, setReason] = useState("");
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [isPopupMessageOpen, setIsPopupMessageOpen] = useState(false);
	const [isGiftPopupOpen, setIsGiftPopupOpen] = useState(false);
	const modalRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [showBlock, setShowBlock] = useState(false);
	const [userPhoto, setUserPhoto] = useState();
	const [userDetails, setUserDetails] = useState();
	const [loadingPhoto, setLoadingPhoto] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isVip, setVip] = useState(false);
	const [faceMatchBusy, setFaceMatchBusy] = useState(false);

	const filePath = getFileBaseUrl();
	const fetchIdRef = useRef(0);
	const expandedRef = useRef(null);

	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
			textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
		}
	};

	useEffect(() => {
		expandedRef.current = expandedUserId;
	}, [expandedUserId]);
	useEffect(() => {
		autoResizeTextarea();
	}, [reason]);

	const fetchUsers = async () => {
		const fetchId = ++fetchIdRef.current;

		try {
			const resp = await getUsersMod(
				pageNumber,
				ITEMS_PER_PAGE,
				filterTerm,
				filterCountry,
				filterCity,
				filterSex,
				4,
			);

			if (fetchId !== fetchIdRef.current) return; // stale response — a newer fetch owns the list

			const total = resp?.totalCount ?? resp?.TotalCount ?? 0; // ✅
			const data = resp?.data ?? resp?.Data ?? []; // ✅
			const tp = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

			setUsers(data);
			setTotalPages(tp);
			setTotalCount(total);
			if (pageNumber > tp) setPageNumber(tp); // page went out of range — snap to the last page
		} catch (e) {
			if (fetchId !== fetchIdRef.current) return;
			setUsers([]);
		} finally {
			if (fetchId === fetchIdRef.current) setLoading(false);
		}
	};

	const handleViewPhoto = (src) => {
		try {
			setUserPhoto(src);
			setShowModal(true);
		} catch (error) {
			console.error("Error fetching photos:", error);
		}
	};

	const fetchUserVideo = async (userId) => {
		try {
			const videoData = await getVideo(userId);

			if (expandedRef.current !== userId) return;
			if (videoData && videoData.fileName) {
				const videoUrl = `${filePath}/Video/${userId}/${videoData.fileName}`;
				setVideoId(videoData.id);
				setVideoUrl(videoUrl); // Set video URL in state
				setVideoIsModerated(videoData.moderated);
			}
		} catch (e) {
			if (expandedRef.current !== userId) return;
			setVideoId(0);
			setVideoUrl("");
			setVideoIsModerated(false);
			setIsPlaying(false);
			setIsMuted(true);
		}
	};

	const fetchUserPhotos = async (userId) => {
		try {
			const resp = await getPhotos(userId);
			if (expandedRef.current !== userId) return;
			setUserPhotos(resp ?? []);
		} catch (e) {
			if (expandedRef.current !== userId) return;
			setUserPhotos([]);
		}
	};

	const fetchUserModDetails = async (userId) => {
		try {
			const resp = await getUsersModDetails(userId);
			if (expandedRef.current !== userId) return;
			setUserDetails(resp ?? null);
		} catch (e) {
			if (expandedRef.current !== userId) return;
			setUserDetails(null);
		}
	};

	const handleRecomputeFaceMatch = async (userId) => {
		if (!userId || faceMatchBusy) return;
		setFaceMatchBusy(true);
		try {
			const updated = await recomputeFaceMatch(userId);
			if (expandedRef.current === userId && Array.isArray(updated)) {
				setUserPhotos(updated);
			}
		} catch (err) {
			console.error("recomputeFaceMatch failed", err);
		} finally {
			setFaceMatchBusy(false);
		}
	};

	const fetchUserSelfie = async (userId) => {
		try {
			const resp = await getSelfie(userId);
			if (expandedRef.current !== userId) return;
			setUserSelfie(resp);
		} catch (e) {
			if (expandedRef.current !== userId) return;
			setUserSelfie(undefined);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, [pageNumber, filterTerm, filterCountry, filterCity, filterSex]);

	const handleExpandClick = (userId) => {
		setExpandedUserId((prev) => {
			const isClosing = prev === userId;

			if (isClosing) {
				// закрываем: никаких API
				setEditUser(undefined);
				setExpandedUserLang("ru");
				setUserPhotos([]);
				setUserDetails(undefined);
				setUserPhotos([]);
				setUserSelfie(undefined);
				setVideoId(0);
				setVideoUrl("");
				setVideoIsModerated(false);
				setIsPlaying(false);
				setIsMuted(true);
				return null;
			}

			// открываем: только тут API
			const user = users.find((u) => u.id === userId);
			setEditUser(user);
			setExpandedUserLang(user?.lang ?? "ru");

			setVip([0, 1].includes(user?.type));
			// очистить старые данные, чтобы не мигало
			setUserPhotos([]);
			setUserSelfie(undefined);
			setVideoId(0);
			setVideoUrl("");
			setVideoIsModerated(false);
			setIsPlaying(false);
			setIsMuted(true);
			setUserDetails(undefined);

			fetchUserPhotos(userId);
			fetchUserSelfie(userId);
			fetchUserVideo(userId);
			fetchUserModDetails(userId);

			return userId;
		});
	};

	const handleButton = (type) => {
		setUserButtonData({ type, id: 0, userId: expandedUserId });
		if (type === "block") {
			setShowBlock(true);
		} else setIsConfirmPopupOpen(true);
	};

	const handleEditData = () => {
		setIsPopupOpen(true);
	};
	const handleOrderData = () => {
		setIsOrderPopupOpen(true);
	};
	const handleUserFirstUse = () => {
		setUserFirstUsePopupOpen(true);
	};

	const removeUserFromLists = (userId) => {
		setUsers((prev) => prev.filter((u) => u.id !== userId));

		if (expandedUserId === userId) {
			setExpandedUserId(null);
			setExpandedUserLang("ru");
			setUserPhotos([]);
			setUserSelfie();
			setVideoId(0);
			setVideoUrl("");
			setUserDetails(undefined);
			setVideoIsModerated(false);
		}
	};
	const hasUnmoderated = ({ photos, selfie, video }) => {
		const photosPending =
			Array.isArray(photos) && photos.some((p) => p?.moderated === false);
		const selfiePending = !!selfie && selfie.moderated === false;
		const videoPending = !!video && video.moderated === false;
		return photosPending || selfiePending || videoPending;
	};

	const refreshUser = async (userId) => {
		if (!userId) return;

		try {
			const [photosResp, selfieResp, videoResp, userData] =
				await Promise.allSettled([
					getPhotos(userId),
					getSelfie(userId),
					getVideo(userId),
					getUsersModDetails(userId),
				]);

			// ✅ if any request failed — don’t remove user by mistake
			const canDecide =
				photosResp.status === "fulfilled" &&
				selfieResp.status === "fulfilled" &&
				videoResp.status === "fulfilled";

			const photos =
				photosResp.status === "fulfilled" ? (photosResp.value ?? []) : [];
			const selfie =
				selfieResp.status === "fulfilled" ? selfieResp.value : null;
			const video = videoResp.status === "fulfilled" ? videoResp.value : null;
			const details = userData.status === "fulfilled" ? userData.value : null;

			// update UI only for expanded user
			if (expandedUserId === userId) {
				setUserPhotos(Array.isArray(photos) ? photos : []);
				setUserSelfie(selfie);
				setUserDetails(details);

				if (video?.fileName) {
					setVideoId(video.id);
					setVideoIsModerated(!!video.moderated);
					setVideoUrl(`${filePath}/Video/${userId}/${video.fileName}`);
				} else {
					setVideoId(0);
					setVideoIsModerated(false);
					setVideoUrl("");
				}
			}

			if (canDecide && !hasUnmoderated({ photos, selfie, video })) {
				removeUserFromLists(userId);
			}
		} catch (e) {
			console.error("refreshUser failed:", e);
		}
	};

	const handleByType = (type, id, userId) => {
		setUserButtonData({ type, id, userId });
		setIsConfirmPopupOpen(true);
	};

	const handleConfirmPopup = async () => {
		if (!userButtonData) return;
		const { type, id, userId } = userButtonData;
		try {
			if (type === "photoDelete") {
				await handleDeletePhoto(id, userId);
			} else if (type === "selfieDelete") {
				await handleDeleteSelfie(id, userId);
			} else if (type === "END") {
				await endOrders(userId);
				setVip(false);
			} else if (type === "videoDelete") {
				await handleDeleteVideo(id, userId);
			} else if (type === "userDelete") {
				await deleteUser(userId);
				await refreshUser(userId);
			} else if (type === "sendMsg") {
				const data = {
					chatId: userId,
					messageKind: 3,
					message: message,
					language: "ru",
				};
				await sendToTelegram(data);
				setMessage("");
			}
		} catch (error) {
			console.error("Confirm action failed:", error);
		} finally {
			setIsConfirmPopupOpen(false);
		}
	};

	const handleDeletePhoto = async (id, userId) => {
		await deletePhoto(id);
		await refreshUser(userId);
	};

	const handleDeleteSelfie = async (id, userId) => {
		await deleteSelfie(id);
		await refreshUser(userId);
	};

	const handleDeleteVideo = async (id, userId) => {
		await deleteVideo(id);
		await refreshUser(userId);
	};

	const toggleFilterModal = (type) => {
		setIsFilterModalOpen(type);
	};
	const toggleBlockModal = () => {
		setShowBlock(!showBlock);
	};

	const applyBlock = async () => {
		// Причина обязательна: блокировку без неё потом не объяснить, и бэкенд
		// её отклоняет.
		const blockReason = normalizeBlockReason(reason);
		if (!blockReason) {
			alert(BLOCK_REASON_REQUIRED_TEXT);
			return;
		}
		try {
			await blockUser({ userId: expandedUserId, reason: blockReason });
			setUsers((p) => p.filter((u) => u.id !== expandedUserId));
			setShowBlock(false);
			setReason("");
		} catch (e) {
			console.error(e);
			alert("Не удалось заблокировать");
		}
	};
	const handleFilterRemove = () => {
		setFilterCountry("");
		setFilterCity("");
		setFilterSex(0); // Reset the sex filter (to an invalid value)
		setPageNumber(1);
	};

	const applyFiltersHandler = () => {
		setPageNumber(1);
		toggleFilterModal(false);
	};
	const handleClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsFilterModalOpen(false);
		}
	};

	useEffect(() => {
		if (isFilterModalOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isFilterModalOpen]);

	if (loading) {
		return <div>Загрузка...</div>;
	}

	const closePopup = () => {
		setIsPopupOpen(false);
	};
	const confirmDel = () => {
		setUsers((prevUsers) =>
			prevUsers.filter((user) => user.id !== expandedUserId),
		);

		setIsPopupOpen(false);
	};
	const closeMessagePopup = () => {
		setIsPopupMessageOpen(false);
	};

	const closeConfirmPopup = () => {
		setIsConfirmPopupOpen(false);
	};
	const closeConfirmGiftPopup = () => {
		setIsGiftPopupOpen(false);
	};

	const handleMasage = () => {
		setIsPopupMessageOpen(true);
	};

	const gifthandler = () => {
		setIsGiftPopupOpen(true);
	};
	const endgifthandler = () => {
		setUserButtonData({ type: "END", id: 0, userId: expandedUserId });
		setIsConfirmPopupOpen(true);
	};

	const handleInputChange = (event) => {
		setReason(event.target.value);
	};

	const handleTogglePlay = () => {
		if (videoRef.current) {
			if (videoRef.current.paused) {
				videoRef.current.play();
				setIsPlaying(true);
			} else {
				videoRef.current.pause();
				setIsPlaying(false);
			}
		}
	};

	const handleToggleMute = () => {
		if (videoRef.current) {
			videoRef.current.muted = !videoRef.current.muted;
			setIsMuted(videoRef.current.muted);
		}
	};

	const handleVideoEnd = () => {
		setIsPlaying(false);
	};
	const makeBigger = () => {
		setBigger(!bigger);
	};
	// Pagination handlers

	const handlePreviousPage = () => {
		if (pageNumber > 1) {
			setPageNumber((prev) => prev - 1);
		}
	};

	const handleNextPage = () => {
		if (pageNumber < totalPages) {
			setPageNumber((prev) => prev + 1);
		}
	};
	const handleSearchChange = (e) => {
		setFilterTerm(e.target.value);
		setPageNumber(1); // Reset to first page when search term changes
	};
	const handleFileUpload = async (e, userId) => {
		const file = e.target.files?.[0];
		if (!file || loadingPhoto) return;

		setLoadingPhoto(true);
		setUploadProgress(0);

		try {
			const hasPhotos = userPhotos?.length > 0;

			// Если нет фото → делаем это главным
			const isMain = !hasPhotos;
			const response = await setPhoto(
				file,
				userId,
				file.name,
				isMain, // не делаем "главным" (можешь поменять на true при желании)
				0, // 0 - создать новое фото
				(p) => setUploadProgress(p),
			);

			const newPhoto = {
				id: response.id || Date.now(),
				fileName: response.fileName,
				moderated: true, // пока только что загружено
				userId: expandedUserId,
				createDate: Date.now(),
				isMain: isMain,
			};
			setUserPhotos((prev) => [...prev, newPhoto]);
		} catch (err) {
			console.error("Error uploading photo:", err);
			alert("Ошибка при загрузке фото");
		} finally {
			setLoadingPhoto(false);
			setUploadProgress(0);
			if (e?.target) e.target.value = ""; // чтобы можно было выбрать тот же файл ещё раз
		}
	};

	return (
		<div className="AllProfiles">
			{showModal && (
				<PhotoModal photo={userPhoto} onClose={() => setShowModal(false)} />
			)}

			<ConfirmPopup
				isOpen={isConfirmPopupOpen}
				onConfirm={handleConfirmPopup}
				onCancel={closeConfirmPopup}
			/>
			<PopupGift
				isOpen={isGiftPopupOpen}
				onClose={closeConfirmGiftPopup}
				setVip={setVip}
				userId={expandedUserId}
			/>
			<Popup
				isOpen={isPopupOpen}
				onClose={closePopup}
				editData={editUser}
				userDetails={userDetails}
				onConfirm={confirmDel}
			/>

			<OrderPopup
				userId={expandedUserId}
				visible={isOrderPopupOpen}
				onClose={() => setIsOrderPopupOpen(false)}
			/>

			<UserFirstUsePopup
				userId={expandedUserId}
				visible={isUserFirstUsePopupOpen}
				onClose={() => setUserFirstUsePopupOpen(false)}
			/>
			<PopupMessage
				isOpen={isPopupMessageOpen}
				onClose={closeMessagePopup}
				type={0}
				sex={filterSex}
			/>

			<div className="filter-button">
				<button onClick={() => toggleFilterModal(true)}>Фильтровать</button>
				<button onClick={handleFilterRemove}>Удалить фильтры</button>
			</div>
			<div className="filter-button">
				<button onClick={handleMasage}>Написать из этого списка</button>
			</div>
			<div className="search-bar">
				<input
					type="text"
					placeholder="Поиск: id, tg, почта, имя, никнейм"
					value={filterTerm}
					onChange={handleSearchChange}
					className="search-input"
				/>
			</div>
			{showBlock && (
				<div className="filter-modal">
					<div className="filter-modal-content">
						<h3>Заблокировать пользователя?</h3>
						<p>Напишите причину</p>
						<textarea
							id="message"
							className={"textArea"}
							name="message"
							value={reason}
							onChange={handleInputChange}
							ref={textareaRef}
						/>

						{!isBlockReasonValid(reason) && (
							<p className="block-reason-hint">
								{BLOCK_REASON_REQUIRED_TEXT}
							</p>
						)}

						<button
							onClick={applyBlock}
							disabled={!isBlockReasonValid(reason)}
						>
							Заблокировать
						</button>
						<button onClick={toggleBlockModal}>Отмена</button>
					</div>
				</div>
			)}
			{isFilterModalOpen && (
				<div className="filter-modal">
					<div className="filter-modal-content" ref={modalRef}>
						<h3>Фильтр</h3>
						<input
							type="text"
							placeholder="Страна"
							value={filterCountry}
							onChange={(e) => setFilterCountry(e.target.value)}
							className="filter-input"
						/>
						<input
							type="text"
							placeholder="Город"
							value={filterCity}
							onChange={(e) => setFilterCity(e.target.value)}
							className="filter-input"
						/>
						<select
							value={filterSex}
							onChange={(e) => setFilterSex(Number(e.target.value))}
							className="filter-input"
						>
							<option value={0}>Фильтр по полу</option>
							<option value={1}>Мужчина</option>
							<option value={2}>Женщина</option>
						</select>
						<button onClick={applyFiltersHandler}>Приминить</button>
						<button onClick={() => toggleFilterModal(false)}>Отмена</button>
					</div>
				</div>
			)}
			{users.length == 0 ? <div>Нет профилей</div> : ""}
			{users.map((user) => (
				<div key={user.id} className="profile-card">
					<div
						className="profile-summary"
						onClick={() => handleExpandClick(user.id)}
						style={{
							backgroundColor: user.sex === 1 ? "#e6f0ff" : "#fff0f6", // Light blue or light pink
							borderRadius: "5px", // Optional: softens edges
						}}
					>
						<div className="profile-summary-cont">
							<h2>
								{user.fullName} (id: {user.id}{user.legacyUserId ? `, tg: ${user.legacyUserId}` : ''})
							</h2>
							<p> Никнейм: {user.userName}</p>
							<p> Почта: {user.mail || "—"}</p>
							<p> Пол: {user.sex === 1 ? "Мужчина" : "Женщина"}</p>
						</div>
						{/* Плашку рисуем только для настоящих тарифов. Прежнее условие
						    прятало её лишь при -1, а всё остальное объявляло Premium,
						    поэтому заказ инкогнито (тип 4) показывался премиумом. */}
						{(user.type === 0 || user.type === 1) && (
							<p className={`userplan ${user.type === 0 ? "plus" : "premium"}`}>
								{user.type === 0 ? "PLUS" : "PREMIUM"}
							</p>
						)}
					</div>
					{expandedUserId === user.id && (
						<div className="profile-details">
							<div className="details-info">
								<div className="info-text">
									<p>
										Дата рождения: {utcToDate(user.birthday)} (Возраст:{" "}
										{user.age})
									</p>
									<p>Страна: {userDetails?.country}</p>
									<p>Город: {userDetails?.city}</p>

									<p>Почта: {userDetails?.mail || "-"}</p>
									<p>OTP: {userDetails?.otp || "-"}</p>
									<p>
										OTP Подтверждён: {userDetails?.otpConfirmed ? "Да" : "Нет"}
									</p>
									{userDetails?.wRole && <p>Роль: {t(userDetails.wRole)}</p>}
									{userDetails?.profession && (
										<p>Дейтельность: {t(userDetails.profession)}</p>
									)}
									<p>О себе: {userDetails?.description}</p>
									<p>Цель знакомства: {t(userDetails?.propose)}</p>
									<p>
										Дата Регистрации: {utcToLocalDateTime(userDetails?.cDate)}
									</p>
									<p>
										Дата Последнего входа:{" "}
										{utcToLocalDateTime(user.lastLoginDate)}
									</p>
									{(userDetails?.cntReg > 1 || userDetails?.cntDel > 0) && (
										<p>
											{userDetails?.cntReg > 1 &&
												`Кол. регистраций: ${userDetails.cntReg} `}
											{userDetails?.cntDel > 0 &&
												`${user.sex === 2 ? "Удалилась" : "Удалился"}: ${userDetails.cntDel}`}
										</p>
									)}
								</div>

								<div className="editpayments">
									<button
										className="info-button"
										onClick={() => handleEditData()}
									>
										Редактировать
									</button>
									<button
										className="payments"
										onClick={() => handleOrderData()}
									>
										Покупки
									</button>
									<button
										className="firstUse"
										onClick={() => handleUserFirstUse()}
									>
										Одноразки
									</button>
								</div>
							</div>
							<div className="photos">
								{userSelfie ? (
									<div className="photoslot">
										<div className="photoslot-inner">
											<img
												style={{ border: "5px solid red" }}
												src={`${filePath}/Selfie/${user?.id}/${userSelfie.fileName}`}
												onClick={() =>
													handleViewPhoto(
														`${filePath}/Selfie/${user?.id}/${userSelfie.fileName}`,
													)
												}
												alt={`Selfie`}
											/>
											<button
												onClick={() =>
													handleByType(
														"selfieDelete",
														userSelfie.id,
														user.id,
													)
												}
											>
												Удалить
											</button>
										</div>
									</div>
								) : (
									<div>
										<h1 style={{ color: "red" }}>Нет селфи</h1>
									</div>
								)}
								{userPhotos.length > 0 ? (
									userPhotos.map((photo, index) => {
										const pct = typeof photo.matchPercent === "number" ? photo.matchPercent : null;
										const tier = pct == null ? "none" : pct >= 50 ? "high" : pct >= 30 ? "medium" : "low";
										const tierColor = tier === "high" ? "#2e7d32" : tier === "medium" ? "#ed6c02" : tier === "low" ? "#c62828" : "#777";
										return (
											<div key={photo.id} className="photoslot">
												<div className="photoslot-inner" style={{ position: "relative" }}>
													<img
														src={`${filePath}/Photo/${user?.id}/${photo.fileName}`}
														onClick={() =>
															handleViewPhoto(
																`${filePath}/Photo/${user?.id}/${photo.fileName}`,
															)
														}
														alt={`Photo ${index + 1}`}
													/>
													<div
														title={
															pct == null
																? "Лицо не сверено"
																: `Совпадение с селфи: ${pct.toFixed(0)}%`
														}
														style={{
															position: "absolute",
															top: 4,
															left: 4,
															padding: "2px 6px",
															borderRadius: 4,
															background: tierColor,
															color: "white",
															fontSize: 12,
															fontWeight: 600,
														}}
													>
														{pct == null ? "?" : `${pct.toFixed(0)}%`}
													</div>
													<button
														onClick={() =>
															handleByType("photoDelete", photo.id, user.id)
														}
													>
														Удалить
													</button>
												</div>
											</div>
										);
									})
								) : (
									<div>
										<h1 style={{ color: "red" }}>Нет фоток</h1>
									</div>
								)}
								<div style={{ width: "100%", marginTop: 8 }}>
									<button
										type="button"
										disabled={faceMatchBusy || !userSelfie?.fileName || userPhotos.length === 0}
										onClick={() => handleRecomputeFaceMatch(user.id)}
										title={
											!userSelfie?.fileName
												? "Сначала загрузите селфи"
												: "Сверить лицо селфи с фото"
										}
										style={{ padding: "6px 12px" }}
									>
										{faceMatchBusy ? "Считаем..." : "Сверить лицо"}
									</button>
								</div>
							</div>
							<div className="upload-photo">
								<label>
									Добавить фото для этого пользователя:{" "}
									<input
										type="file"
										accept="image/*"
										disabled={loadingPhoto}
										onChange={(e) => handleFileUpload(e, user.id)}
									/>
								</label>
								{loadingPhoto && (
									<div style={{ marginTop: 8 }}>
										Загрузка: {uploadProgress}%
									</div>
								)}
							</div>
							{videoUrl && (
								<div className="video-player">
									<video
										ref={videoRef} // ✅ Connect the ref
										autoPlay={false} // ❌ Remove autoplay to control manually
										muted={true} // ✅ Ensure it starts muted
										playsInline
										src={videoUrl}
										onEnded={handleVideoEnd}
										className={bigger ? "video-previewbig" : "video-preview"}
										onClick={makeBigger}
									/>

									<div className="but-div">
										<button
											style={{ background: "blue" }}
											onClick={handleTogglePlay}
										>
											{isPlaying ? "PAUSE" : "PLAY"}
										</button>
										<button
											style={{ background: "orange" }}
											onClick={handleToggleMute}
										>
											{isMuted ? "UNMUTE" : "MUTE"}
										</button>
										<button
											style={{ background: "red" }}
											onClick={() =>
												handleByType("videoDelete", videoId, user.id)
											}
										>
											Удалить
										</button>
									</div>
								</div>
							)}

							<div className="actions">
								<button
									style={{ background: "red" }}
									onClick={() => handleButton("block")}
								>
									Заблокировать
								</button>
							</div>
							<div className="orderButtons">
								<button
									className="gift second"
									onClick={endgifthandler}
									disabled={!isVip}
								>
									Убрать подписки
								</button>
								{/* Открыто и для подписчика: сроки прибавляются к тому, что
								уже есть, так что это же окно продлевает подписку. */}
								<button className="gift" onClick={gifthandler}>
									{isVip ? "Обновить подписку" : "Подарить Jerm Plus/Premium"}
								</button>
							</div>
							<div className="actions">
								<button
									style={{ background: "red" }}
									onClick={() => handleButton("userDelete")}
								>
									Удалить
								</button>
							</div>
							<textarea
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder={
									user.legacyUserId
										? "Напишите текст сообщения"
										: "Пользователь без Telegram — отправка недоступна"
								}
								disabled={!user.legacyUserId}
							/>
							<div className="actions">
								<button
									onClick={() => handleButton("sendMsg")}
									disabled={!user.legacyUserId || !message.trim()}
								>
									Отправить сообщение
								</button>
							</div>
						</div>
					)}
				</div>
			))}
			<div className="pagination">
				<button onClick={handlePreviousPage} disabled={pageNumber === 1}>
					Назад
				</button>
				<span>
					Страница {pageNumber} из {totalPages}
				</span>
				<button onClick={handleNextPage} disabled={pageNumber === totalPages}>
					Дальше
				</button>
			</div>
		</div>
	);
};

export default AllProfiles;
